// support — feature detection for the timelapse mp4 export (T2 slice;
// growth-timelapse-share-2026-07-02.md §5.2 「WebCodecs 非対応環境では
// ボタン非表示」).
//
// T3 calls `isTimelapseExportSupported()` once (result is cached — the
// answer can't change within a page lifetime) and simply doesn't render
// the share entry point when it resolves `false`. The check is REAL
// (`VideoEncoder.isConfigSupported` on the exact candidate configs we'd
// encode with, at a representative 720p30) rather than a `typeof` sniff,
// because Safari/Firefox ship the WebCodecs interface without guaranteeing
// an H.264 encoder.
//
// `detectTimelapseExportSupport` takes the globals as a parameter object so
// the node-env unit tests can exercise every branch without WebCodecs.

import { buildEncoderConfigCandidates } from './encoderConfig.js';

/** Probe dimensions — representative of the real default output (720p 級 30fps). */
const PROBE = { width: 1280, height: 720, fps: 30 } as const;

/** The subset of globals the export pipeline requires. */
export interface TimelapseSupportGlobals {
  readonly VideoEncoder?: {
    isConfigSupported(config: VideoEncoderConfig): Promise<{ supported?: boolean }>;
  };
  readonly VideoFrame?: unknown;
  readonly OffscreenCanvas?: unknown;
  readonly Worker?: unknown;
}

/**
 * `true` when every pipeline dependency exists AND at least one of our
 * H.264 candidate configs is encodable. `isConfigSupported` rejections
 * (some UAs throw on unknown fields) count as "unsupported candidate",
 * not as an error.
 */
export const detectTimelapseExportSupport = async (
  globals: TimelapseSupportGlobals,
): Promise<boolean> => {
  if (
    globals.VideoEncoder === undefined ||
    globals.VideoFrame === undefined ||
    globals.OffscreenCanvas === undefined ||
    globals.Worker === undefined
  ) {
    return false;
  }
  for (const config of buildEncoderConfigCandidates(PROBE)) {
    try {
      const res = await globals.VideoEncoder.isConfigSupported(config);
      if (res.supported === true) return true;
    } catch {
      // Candidate rejected outright — try the next profile.
    }
  }
  return false;
};

let cached: Promise<boolean> | null = null;

/** Cached runtime check against the real globals (see module header). */
export const isTimelapseExportSupported = (): Promise<boolean> => {
  if (cached === null) {
    cached = detectTimelapseExportSupport(globalThis as unknown as TimelapseSupportGlobals).catch(
      () => false,
    );
  }
  return cached;
};

/** Test hook — clear the memoized answer (module-level cache). */
export const resetTimelapseExportSupportCache = (): void => {
  cached = null;
};
